import React, { useState, useEffect, useRef } from "react";
import Picker from "emoji-picker-react";
import axiosInstance, { baseWebSocketURL } from "../axiosInstance.js";
import { localizeKey } from "../localize.js";
import { useNavigate } from "react-router-dom";
import { Button } from "@mantine/core";

export default function MyChatComponent({ chatId, language, myUserInfo, _userChatInfo, chatOwnerInfo, isDropdown=false, fillHeight=false }) {
  const [messages, setMessages] = useState([]);
  const [messageText, setMessageText] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const [typingNick, setTypingNick] = useState(null);
  const [editingMessage, setEditingMessage] = useState(null); // { messageId, text }
  const [userChatInfo, setUserChatInfo] = useState(_userChatInfo);

  const ws = useRef(null);
  const inputRef = useRef(null);
  const navigate = useNavigate();
  const typingTimeoutRef = useRef(null); // Add a ref to manage the typing timeout
  const draftTimeoutRef = useRef(null);
  const lastSavedDraftRef = useRef("");
  const draftLoadedRef = useRef(false);

  /* ------------------------------------------------------- */
  /* Auto-scroll down                                        */
  /* ------------------------------------------------------- */
  const messagesEndRef = useRef(null);
  const scrollRef = useRef(null);
  const [shouldAutoScroll, setShouldAutoScroll] = useState(true);
  useEffect(() => {
    if (shouldAutoScroll) {
      messagesEndRef.current?.scrollIntoView();
    }
  }, [messages]);
  // Hack when message is sent already.
  const scrollMessagesToBottom = () => {
    const scrollEl = scrollRef.current;
    if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;
  };

  /* ------------------------------------------------------- */
  /* Message Menu                                            */
  /* ------------------------------------------------------- */
  const [menuOpenFor, setMenuOpenFor] = useState(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest(".message-menu")) {
        setMenuOpenFor(null);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  /* ------------------------------------------------------- */
  /* Suggested Users                                         */
  /* ------------------------------------------------------- */
  const fetchUsers = (query) => {
    axiosInstance
      .get(`/searchUsers?userName=${encodeURIComponent(query)}`)
      .then((res) => setSuggestions(res.data || []))
      .catch(() => setSuggestions([]));
  };

  const onInputChange = (e) => {
    const val = e.target.value;
    const caret = e.target.selectionStart;
    setMessageText(val);

    const mention = findActiveMention(val, caret);
    if (mention) {
      fetchUsers(mention.fragment);
    } else {
      setSuggestions([]);
    }

    if (ws.current?.readyState === 1) {
      ws.current.send("_" + myUserInfo.myUserName);
    }
  };

  const selectUser = (user) => {
    const el = inputRef.current;
    if (!el) return;

    const caret = el.selectionStart;
    const mention = findActiveMention(messageText, caret);
    if (!mention) return;

    const { newText, newCaret } = replaceMention(
      messageText,
      mention.start,
      mention.end,
      user.userName
    );

    setMessageText(newText);
    setSuggestions([]);

    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = newCaret;
    }, 0);
  };

  /* ------------------------------------------------------- */
  /* Load Messages                                           */
  /* ------------------------------------------------------- */
  const fetchMessages = () => {
    axiosInstance
      .get(`/messages?chatId=${chatId}&chatOwnerId=${chatOwnerInfo.id}`)
      .then(res => setMessages(res.data.reverse()))
      .catch(() => {});
  };

  const fetchDraft = () => {
    axiosInstance
      .get(`/messages/drafts?chatId=${chatId}`)
      .then((res) => {
        const text = res.data?.text || "";
        setMessageText(text);
        lastSavedDraftRef.current = text;
      })
      .catch(() => {})
      .finally(() => {
        draftLoadedRef.current = true;
      });
  };

  const saveDraft = (text) => {
    if (!myUserInfo || !chatId) return;
    if (lastSavedDraftRef.current === text) return;

    lastSavedDraftRef.current = text;
    axiosInstance.post(`/messages/drafts`, { chatId, text }).catch(() => {});
  };

  const scheduleDraftSave = (text) => {
    clearTimeout(draftTimeoutRef.current);
    draftTimeoutRef.current = setTimeout(() => saveDraft(text), 700);
  };

  /* ------------------------------------------------------- */
  /* Init WebSocket                                          */
  /* ------------------------------------------------------- */
  const initWebSocket = () => {
    if (ws.current && (ws.current.readyState === 1 || ws.current.readyState === 0)) return;

    ws.current = new WebSocket(`${baseWebSocketURL}/chat/${chatId}`);

    ws.current.onmessage = (e) => {
      if (e.data.startsWith("_")) {
        const nick = e.data.substring(1);
        if (nick !== myUserInfo.myUserName) {
          setTypingNick(nick);

          // Clear the previous timeout and set a new one
          clearTimeout(typingTimeoutRef.current);
          typingTimeoutRef.current = setTimeout(() => setTypingNick(null), 3000); 
        }
        return;
      }
      fetchMessages();
    };
  };

  /* ------------------------------------------------------- */
  /* Message Edit via main input                              */
  /* ------------------------------------------------------- */
  const startEdit = (msg) => {
    setEditingMessage({ messageId: msg.messageId, text: msg.text });
    setMessageText(msg.text);
    setTimeout(() => inputRef.current?.focus(), 0);
  };

  const cancelEdit = () => {
    setEditingMessage(null);
    // setMessageText("");
  };

  const saveEdit = () => {
    if (!editingMessage || !messageText.trim()) return;

    axiosInstance
      .put(`/messages/${editingMessage.messageId}`, { text: messageText })
      .then((res) => {
        if (res.status === 200) {
          setMessages((prev) =>
            prev.map((msg) =>
              msg.messageId === editingMessage.messageId
                ? { ...msg, text: messageText, updatedTime: res.data.updatedTime }
                : msg
            )
          );
          setMessageText("");

          // ADDITIONAL MOBILE-FRIENDLY SCROLL
          setTimeout(scrollMessagesToBottom, 50);
        }
      })
      .catch((e) => console.error(e))
      .finally(() => {
        cancelEdit();
      });
  };

  /* ------------------------------------------------------- */
  /* Handlers                                                */
  /* ------------------------------------------------------- */
  const sendMessage = () => {
    if (!messageText.trim()) return;

    axiosInstance
      .post(`/messages?chatId=${chatId}&chatOwnerId=${chatOwnerInfo.id}`, {
        text: messageText,
        chatId,
        chatOwnerType: chatOwnerInfo.type,
        chatOwnerId: chatOwnerInfo.id
      })
      .then(res => {
        if (res.status === 200) {
          setMessages(prev => [...prev, res.data]);
          setMessageText("");
          clearTimeout(draftTimeoutRef.current);
          saveDraft("");

          // ADDITIONAL MOBILE-FRIENDLY SCROLL
          setTimeout(scrollMessagesToBottom, 50);
        }
      });
  };

  const subscribe = () => {
    axiosInstance.post(`/chats/${chatId}/participation`).then(() => {
      setUserChatInfo((prev) => ({...prev, participates: true}));
    });
  };

  const unsubscribe = () => {
    axiosInstance.delete(`/chats/${chatId}/participation`).then(() => {
     setUserChatInfo((prev) => ({...prev, participates: false}));
    });
  };

  const getLastOwnMessage = () => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const m = messages[i];
      if (m.fromUserId === myUserInfo.myUserId && !m.deleted) {
        return m;
      }
    }
    return null;
  };

  /* ------------------------------------------------------- */
  /* Effects                                                 */
  /* ------------------------------------------------------- */
  useEffect(() => {
    if (!myUserInfo) return;

    initWebSocket();
    fetchMessages();
    fetchDraft();

    const interval = setInterval(() => initWebSocket(), 3000);
    return () => {
      if (ws.current?.readyState === 1 || ws.current?.readyState === 0) {
        ws.current.close();
      }
      clearInterval(interval);
    };
  }, [chatId, myUserInfo?.myUserId, chatOwnerInfo?.id]);

  useEffect(() => {
    if (!chatId) return;
    setMessageText("");
    setSuggestions([]);
    setTypingNick(null);
    setEditingMessage(null);
    lastSavedDraftRef.current = "";
    draftLoadedRef.current = false;
  }, [chatId]);

  // Auto-resize textarea on edit.
  useEffect(() => {
    const el = inputRef.current;
    if (!el) return;
  
    el.style.height = "auto";
    el.style.height = el.scrollHeight + "px";
  }, [messageText]);

  useEffect(() => {
    return () => {
      // Clear the typing timeout on component unmount
      clearTimeout(typingTimeoutRef.current);
      clearTimeout(draftTimeoutRef.current);
    };
  }, []);

  useEffect(() => {
    if (!myUserInfo || !chatId) return;
    if (!draftLoadedRef.current) return;
    scheduleDraftSave(messageText);
  }, [messageText, chatId, myUserInfo?.myUserId]);

  /* ------------------------------------------------------- */
  /* UI Helpers                                              */
  /* ------------------------------------------------------- */
  const linkify = (text) =>
    text.replace(
      /(https?:\/\/[^\s]+)/g,
      (m) => `<a class="chat-link" href="${m}" target="_blank">${m}</a>`
    );

  const mentionify = (t) => t.replace(/@([a-zA-Z0-9_.-]+)/g, `<span class="chat-mention">@$1</span>`);

  const italizeAux = (t) => {
    if (!t.startsWith('aux: ')) return t;

    return `<em class="chat-deleted">${t.slice(5)}</em>`;
  };

  /* ------------------------------------------------------- */
  /* Long tap on messages                                    */
  /* ------------------------------------------------------- */
  const longPressTimeout = useRef(null);

  /* ------------------------------------------------------- */
  /* Emojis                                                  */
  /* ------------------------------------------------------- */
  const [emojiQuery, setEmojiQuery] = useState(null);
  const [filteredEmojis, setFilteredEmojis] = useState([]);

  const onEmojiSelect = (emoji) => {
    const el = inputRef.current;
    const caret = el.selectionStart;
    const before = messageText.slice(0, caret);
    const after = messageText.slice(caret);
    // replace :query with emoji + space
    const newBefore = before.replace(/:[a-zA-Z0-9_]*$/, emoji + " ");
    const newText = newBefore + after;
    setMessageText(newText);
    setEmojiQuery(null);
    setShowEmojiPicker(false);
    setTimeout(() => {
      el.focus();
      const newCaret = newBefore.length;
      el.selectionStart = el.selectionEnd = newCaret;
    }, 0);
  };

  const replyToMessage = (messageText) => {
    const el = inputRef.current;
    if (!el) return;

    const quotedText = `> ${messageText.replace(/\n/g, "\n> ")}\n\n`;
    setMessageText((prev) => prev + quotedText);

    setTimeout(() => {
      el.focus();
      el.selectionStart = el.selectionEnd = quotedText.length;
    }, 0);
  };

  const [modalImage, setModalImage] = useState(null);

  const renderMessage = (m, index) => {
    const isMine = m.fromUserId === myUserInfo.myUserId;
    const isDeleted = m.deleted;
    const nextMessage = messages[index + 1];
    const showAvatar = !isMine && (!nextMessage || nextMessage.fromUserId !== m.fromUserId);
  
    const handleDelete = () => {
      if (!window.confirm(localizeKey("confirmDeleteMessage", language))) return;
      axiosInstance
        .delete(`/messages/${m.messageId}`)
        .then(() => {
          setMessages(prev =>
            prev.map(msg =>
              msg.messageId === m.messageId ? { ...msg, text: localizeKey("deletedBySender", language), deleted: true } : msg
            )
          );
        })
        .catch(console.error);
    };
  
    const timestamp = m.updatedTime ? new Date(m.updatedTime) : new Date(m.sentTime);
    const now = new Date();
    const isToday = timestamp.toDateString() === now.toDateString();
    const isThisYear = timestamp.getFullYear() === now.getFullYear();
  
    const localizedTimestamp = isToday
      ? timestamp.toLocaleTimeString(language === "ru" ? "ru-RU" : "en-US", { hour: '2-digit', minute: '2-digit' })
      : timestamp.toLocaleDateString(language === "ru" ? "ru-RU" : "en-US", {
          hour: '2-digit',
          minute: '2-digit',
          day: "2-digit",
          month: "short",
          ...(isThisYear ? {} : { year: "numeric" })
        });


  
    return (
      <div
        className={`mb-2 flex ${
          isMine ? "justify-end pr-3" : "justify-start pl-2"
        }`}
      >
        <div
          className={`flex gap-1 max-w-[80%] min-w-0 items-end ${
            isMine ? "ml-auto" : "mr-auto"
          }`}
        >
          {/* Avatar */}
          {showAvatar ? (
            <img
              src={m.fromUserAvatarUrl}
              className="w-8 h-8 rounded-full cursor-pointer self-end flex-shrink-0"
              onClick={() => navigate(`/users/${m.fromUserId}`)}
              alt=""
            />
          ) : (
            <div className="w-8 flex-shrink-0" />
          )}
    
          {/* Message block */}
          <div className="flex flex-col relative min-w-0">
            {/* Bubble */}
            <div
              className={`p-2 rounded-xl text-sm whitespace-pre-wrap break-words select-none
                ${isMine ? "cursor-pointer" : ""}
              `}
              style={{
                overflowWrap: "anywhere",
                wordBreak: "break-word",
                background: isMine ? "var(--mantine-primary-color-filled)" : "light-dark(var(--mantine-color-gray-1), var(--mantine-color-default))",
                color: isMine ? "white" : undefined,
                ...(isDeleted ? { fontStyle: "italic", color: "var(--mantine-color-dimmed)" } : {}),
              }}
              onClick={() =>
                isMine && !isDeleted
                  ? setMenuOpenFor(menuOpenFor === m.messageId ? null : m.messageId)
                  : null
              }
              onTouchStart={() => {
                if (!isMine || isDeleted) return;
                longPressTimeout.current = setTimeout(() => {
                  setMenuOpenFor(m.messageId);
                  if (navigator.vibrate) navigator.vibrate(30);
                }, 700);
              }}
              onTouchEnd={() => clearTimeout(longPressTimeout.current)}
              onTouchMove={() => clearTimeout(longPressTimeout.current)}
            >
              <span
                dangerouslySetInnerHTML={{
                  __html: italizeAux(mentionify(linkify(m.text))),
                }}
              />
            </div>
    
            {/* Link preview */}
            {(m.previewTitle || m.previewDescription || m.previewImageUrl) && !isDeleted && (
              <>
                {m.previewTitle || m.previewDescription ? (
                  // Full preview with link
                  <a
                    href={m.previewSourceUrl || "#"}
                    target="_blank"
                    className="inline-flex mt-2 rounded overflow-hidden cursor-pointer"
                    style={{
                      background: "var(--mantine-color-default)",
                      border: "1px solid var(--mantine-color-default-border)",
                    }}
                    onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
                    onMouseOut={(e) => e.currentTarget.style.background = "var(--mantine-color-default)"}
                  >
                    {m.previewImageUrl && (
                      <img
                        src={m.previewImageUrl}
                        className="w-20 h-20 object-cover flex-shrink-0"
                        alt="link preview"
                      />
                    )}
                    <div className="p-2 flex flex-col justify-center text-sm max-w-xs">
                      {m.previewTitle && <div className="font-semibold">{m.previewTitle}</div>}
                      {m.previewDescription && (
                        <div style={{ color: "var(--mantine-color-dimmed)" }} className="truncate">{m.previewDescription}</div>
                      )}
                    </div>
                  </a>
                ) : (
                  // Only image → open modal on click
                  <div
                    className="inline-block mt-2 cursor-pointer"
                    onClick={() => setModalImage(m.previewImageUrl)}
                  >
                    <img
                      src={m.previewImageUrl}
                      className="w-20 h-20 object-cover rounded"
                      style={{ border: "1px solid var(--mantine-color-default-border)" }}
                      alt="link preview"
                    />
                  </div>
                )}

                {/* Modal for full-size image */}
                {modalImage && (
                  <div
                    className="fixed inset-0 bg-black bg-opacity-80 flex items-center justify-center z-50"
                    onClick={() => setModalImage(null)}
                  >
                    <img src={modalImage} className="max-w-full max-h-full" alt="full preview" />
                  </div>
                )}
              </>
            )}
    
            {/* Timestamp + Reply */}
            <div
              className={`text-xs mt-1 flex items-center gap-2 ${
                isMine ? "justify-end" : "justify-start"
              }`}
              style={{ color: "var(--mantine-color-dimmed)" }}
            >
              <span>
                {localizedTimestamp}
                {m.updatedTime && (
                  <span className="ml-1" style={{ color: "var(--mantine-color-dimmed)" }}>
                    ({localizeKey("edited", language)})
                  </span>
                )}
              </span>
    
              {!isDeleted && (
                <span
                  className="cursor-pointer hover:underline"
                  style={{ color: "var(--mantine-color-blue-text)" }}
                  onClick={() => replyToMessage(m.text)}
                >
                  {language === "en" ? "Reply" : "Ответить"}
                </span>
              )}
            </div>
    
            {/* Message menu */}
            {isMine && !isDeleted && menuOpenFor === m.messageId && (
              <div
                className={`message-menu absolute ${
                  isMine ? "right-0" : "left-0"
                } bottom-full mb-1 min-w-max rounded shadow-lg z-50`}
                style={{
                  background: "var(--mantine-color-body)",
                  border: "1px solid var(--mantine-color-default-border)",
                }}
              >
                <div
                  className="px-2 py-1 cursor-pointer text-sm"
                  style={{ }}
                  onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
                  onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                  onClick={() => {
                    startEdit(m);
                    setMenuOpenFor(null);
                  }}
                >
                  {localizeKey("edit", language)}
                </div>
                <div
                  className="px-2 py-1 cursor-pointer text-sm"
                  style={{ color: 'var(--mantine-color-red-text)' }}
                  onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
                  onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
                  onClick={() => {
                    handleDelete();
                    setMenuOpenFor(null);
                  }}
                >
                  {localizeKey("delete", language)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  };


      // console.log("myUserInfo:", myUserInfo);
      // console.log("isDropdown:", isDropdown);

  const containerClassName = fillHeight
    ? "h-full flex flex-col min-h-0 px-3 py-2 relative"
    : "h-[31.2rem] md:h-[40rem] flex flex-col px-3 py-2 relative";

  return (
    <div className={containerClassName}>

      {/* Messages */}
      <div
        className="flex-1 min-h-0 overflow-y-auto relative"
        ref={scrollRef}
        onScroll={() => {
          const el = scrollRef.current;
          if (!el) return;
          setShouldAutoScroll(el.scrollHeight - el.scrollTop - el.clientHeight < 40);
        }}
      >
        {messages.map(renderMessage)}
        <div ref={messagesEndRef} />
      </div>

      {/* Typing indicator */}
      {typingNick && (
        <div className="text-xs pl-2 py-0.5" style={{ color: "var(--mantine-color-dimmed)" }}>
          {typingNick + (language === "ru" ? " печатает..." : " is typing...")}
        </div>
      )}

      {/* Suggestions */}
      {suggestions.length > 0 && (
        <div
          className="absolute bottom-16 left-2 w-64 rounded-xl shadow-lg z-50 max-h-64 overflow-auto"
          style={{
            background: "var(--mantine-color-body)",
            border: "1px solid var(--mantine-color-default-border)",
          }}
        >
          {suggestions.map((u) => (
            <div
              key={u.id}
              onClick={() => selectUser(u)}
              className="flex items-center gap-2 p-2 cursor-pointer"
              onMouseOver={(e) => e.currentTarget.style.background = "var(--mantine-color-default-hover)"}
              onMouseOut={(e) => e.currentTarget.style.background = "transparent"}
            >
              <img src={u.avatarUrl} className="w-7 h-7 rounded-full" alt="" />
              <span className="text-sm">@{u.userName}</span>
            </div>
          ))}
        </div>
      )}

      {/* Slim Emoji-picker */}
      {showEmojiPicker && emojiQuery !== null && filteredEmojis.length > 0 && (
        <div
          className="absolute bottom-16 left-2 right-2 flex space-x-1 p-1 rounded z-50 overflow-x-auto"
          style={{
            background: "var(--mantine-color-body)",
            border: "1px solid var(--mantine-color-default-border)",
          }}
        >
          {filteredEmojis.map((e) => (
            <span
              key={e.name}
              className="cursor-pointer text-xl"
              onClick={() => onEmojiSelect(e.emoji)}
            >
              {e.emoji}
            </span>
          ))}
        </div>
      )}

      {/* Input */}
      {myUserInfo && (
        <div className="relative mt-2">

          {/* Original message above input */}
          {editingMessage && (
            <div className="text-sm mb-1 break-words flex justify-between items-start" style={{ color: "var(--mantine-color-dimmed)" }}>
              <span className="flex-1">
                <span className="font-bold" style={{ color: "var(--mantine-color-blue-text)" }}>
                  {language === "en" ? "Edit message" : "Редактирование сообщения"}
                </span>
                <br />
                {editingMessage.text}
              </span>
              <Button
                variant="subtle"
                size="sm"
                className="ml-2"
                onClick={(e) => {
                  e.stopPropagation();
                  cancelEdit(); // input text stays because we removed setMessageText("")
                }}
              >
                ✕
              </Button>
            </div>
          )}

          <textarea
            ref={inputRef}
            value={messageText}
            onChange={(e) => {
              setMessageText(e.target.value);

              // Auto-resize textarea
              const el = e.target;
              el.style.height = "auto"; // reset height
              el.style.height = el.scrollHeight + "px";

              // Scroll messages down as textarea grows
              const scrollEl = scrollRef.current;
              if (scrollEl) scrollEl.scrollTop = scrollEl.scrollHeight;

              // Check for mentions
              const caret = el.selectionStart;
              const mention = findActiveMention(e.target.value, caret);
              if (mention) fetchUsers(mention.fragment);
              else setSuggestions([]);

              // Typing indicator via WebSocket
              if (ws.current?.readyState === 1) {
                ws.current.send("_" + myUserInfo.myUserName);
              }

              // --- emoji query handling ---
              const val = e.target.value;
              const char = val[caret - 1];
              if (char === ":") {
                setEmojiQuery("");
                setShowEmojiPicker(true);
                setFilteredEmojis(EMOJIS); // show all initially
              } else if (emojiQuery !== null) {
                // we are in emoji-query mode
                // find start of “:query”
                const substr = val.slice(0, caret);
                const match = substr.match(/:([a-zA-Z0-9_]*)$/);
                if (match) {
                  const q = match[1];
                  setEmojiQuery(q);
                  setFilteredEmojis(
                    q === ""
                      ? EMOJIS                          // ← show all emojis when query is empty
                      : EMOJIS.filter((e) =>
                          e.name.startsWith(q.toLowerCase())
                        )
                  );
                } else {
                  // token broken or removed
                  setEmojiQuery(null);
                  setShowEmojiPicker(false);
                }
              }
            }}
            rows={1}
            className="w-full pr-14 pl-2 pt-3 pb-3 rounded resize-none overflow-y-auto max-h-40 focus:outline-none"
            style={{ background: "light-dark(var(--mantine-color-gray-1), var(--mantine-color-default))", color: "var(--mantine-color-text)", border: "light-dark(1px solid var(--mantine-color-default-border), none)" }}
            placeholder={language === "en" ? "Type a message…" : "Напишите сообщение…"}
            onKeyDown={(e) => {
              const isMobile = /Mobi|Android/i.test(navigator.userAgent);

              if (e.key === "Backspace" && emojiQuery !== null) {
                // if query becomes empty, hide
                const caret = e.target.selectionStart;
                const before = messageText.slice(0, caret);
                if (before.match(/:$/)) {
                  setEmojiQuery(null);
                  setShowEmojiPicker(false);
                }
              }
              if (e.key === "Enter" && !e.shiftKey) {
                if (isMobile) {
                  setEmojiQuery(null);
                  setShowEmojiPicker(false);
                  return; // on mobile, let the user tap the Send button
                }

                e.preventDefault();
                editingMessage ? saveEdit() : sendMessage();
                setEmojiQuery(null);
                setShowEmojiPicker(false);
              }
              if (e.key === "Escape" && editingMessage) cancelEdit();
              if (e.key === "Escape" && emojiQuery !== null) {
                setEmojiQuery(null);
                setShowEmojiPicker(false);
              }
              if (e.key === "ArrowUp" && !messageText.trim() && !editingMessage) {
                const last = getLastOwnMessage();
                if (last) startEdit(last);
              }
            }}
          />

          {
          messageText.trim() && (
            <Button
              variant="filled"
              color="blue"
              size="xs"
              style={{ position: 'absolute', right: 6, bottom: 13 }}
              onClick={editingMessage ? saveEdit : sendMessage}
            >
              {/* {editingMessage ? (language === "en" ? "Save" : "Сохранить") : (language === "en" ? "Send" : "Отправить")} */}
              &gt;
            </Button>
          )
          }
        </div>
      )}

    </div>
  );
}

// Finds an active @mention token the user is editing
export function findActiveMention(text, caretPos) {
  const left = text.slice(0, caretPos);
  const at = left.lastIndexOf("@");
  if (at === -1) return null;
  if (at > 0 && !/\s/.test(left[at - 1])) return null;
  const fragment = left.slice(at + 1);
  if (!/^[a-zA-Z0-9_.-]*$/.test(fragment)) return null;
  return { start: at, end: caretPos, fragment };
}

// Replace mention token with @username and restore caret
export function replaceMention(text, start, end, username) {
  const before = text.slice(0, start);
  const after = text.slice(end);
  const inserted = `@${username} `;
  return { newText: before + inserted + after, newCaret: before.length + inserted.length };
}

// small sample emoji dataset — expand as needed
const EMOJIS = [
  { name: "rofl", emoji: "🤣" },
  { name: "smile", emoji: "😄" },
  { name: "grin", emoji: "😁" },
  { name: "joy", emoji: "😂" },
  { name: "heart", emoji: "❤️" },
  { name: "thumbsup", emoji: "👍" },
  { name: "cry", emoji: "😢" },
  { name: "angry", emoji: "😠" },
  { name: "party", emoji: "🥳" },
  { name: "see-no-evil", emoji: "🙈" },
  { name: "laugh", emoji: "😆" },
  { name: "wink", emoji: "😉" },
  { name: "sunglasses", emoji: "😎" },
  { name: "thinking", emoji: "🤔" },
  { name: "clap", emoji: "👏" },
  { name: "pray", emoji: "🙏" },
  { name: "fire", emoji: "🔥" },
  { name: "star", emoji: "⭐" },
  { name: "100", emoji: "💯" },
  { name: "check", emoji: "✔️" },
  { name: "cross", emoji: "❌" },
  { name: "rocket", emoji: "🚀" },
  { name: "tada", emoji: "🎉" },
  { name: "coffee", emoji: "☕" },
  { name: "cake", emoji: "🎂" },
  { name: "dog", emoji: "🐶" },
  { name: "cat", emoji: "🐱" },
  { name: "unicorn", emoji: "🦄" },
  { name: "dragon", emoji: "🐉" },
  // … extend or load from full list
];
